import type { Theme, SxProps } from "@mui/material/styles";
import { GRID_DIMENSIONS } from "@/utils/constants";

/**
 * Helper function to calculate dynamic height for Laboratory tables
 */
export const getLabDynamicHeight = (rowCount?: number): string => {
  let count = rowCount ?? 5;
  if (count < 5) {
    count = 5;
  }
  const PADDING = 76;
  const calculatedHeight =
    GRID_DIMENSIONS.HEADER_HEIGHT +
    count * GRID_DIMENSIONS.ROW_HEIGHT +
    PADDING;
  return `${calculatedHeight}px`;
};

/**
 * Helper function to calculate dynamic height for EAF tables
 */
export const getEAFDynamicHeight = (rowCount?: number): string => {
  const count = rowCount ?? 4;
  return `${Math.min(350 + (count - 4) * 50, 550)}px`;
  // 350px base for 4 rows, 50px for each additional row, maximum 550px (for 8 rows)
};

/**
 * Helper function to calculate dynamic height for Caster Torches tables
 */
export const getTorchesDynamicHeight = (rowCount?: number): string => {
  let count = rowCount ?? 5;
  if (count < 5) {
    count = 5;
  }
  const PADDING = 100;
  const calculatedHeight =
    GRID_DIMENSIONS.HEADER_HEIGHT +
    count * GRID_DIMENSIONS.ROW_HEIGHT +
    PADDING;
  return `${calculatedHeight}px`;
};

/**
 * Shared Data Entry Table styles for all modules (Caster, EAF, LMF, Laboratory)
 * Converted from makeStyles to sx-based styling for MUI v6
 */
export const dataEntryTableStyles = (
  theme: Theme,
): Record<string, SxProps<Theme>> => ({
  // Standard ag-grid container
  agGridBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: "10px 10px 50px 10px",
    height: "352px",
    minHeight: "352px",
  },
  // Small ag-grid container
  smAgGridBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: "10px 10px 10px 10px",
    height: "287px",
    minHeight: "287px",
  },
  // Read-only ag-grid container
  readOnlyAgGridBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: "10px",
    height: "199px",
    minHeight: "199px",
  },
  // Dynamic height container (use with helper functions)
  dynamicHeight: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: "10px 10px 50px 10px",
  } satisfies SxProps<Theme>,
  // EAF-specific: ag-grid with totals row (extra bottom padding)
  agGridBoxWithTotals: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: "10px 10px 80px 10px",
    height: "356px",
    minHeight: "356px",
  },
  // EAF/LMF-specific: small horizontal paper container
  smHorizontalPaper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "4px",
    padding: "10px",
    height: "60px",
    minHeight: "60px",
  },
  // EAF/Torches-specific: totals row
  totalsRow: {
    display: "flex",
    width: "100%",
    marginTop: theme.spacing(0.5),
  },
  // EAF/Torches-specific: total cell
  totalCell: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(0.5),
    fontWeight: "bold",
  },
  // Torches-specific: button group
  buttonGroup: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "20px",
  } satisfies SxProps<Theme>,
  // Torches-specific: default button
  defaultButton: {
    padding: "6px 16px",
    fontSize: "0.875rem",
    lineHeight: 1.75,
    borderRadius: "4px",
  } satisfies SxProps<Theme>,
});
